import { Component, EventEmitter, Input, OnInit, Output, OnChanges, SimpleChanges } from '@angular/core';
import { Assessor } from 'src/app/models/Assessor';
import { Voter } from 'src/app/models/Voter';

@Component({
  selector: 'app-poll-assessors',
  templateUrl: 'poll-assessors.component.html',
  styleUrls: ['./poll-assessors.component.less', '../../../../shared/common.less'],
})

export class PollAssessorsComponent implements OnInit, OnChanges {
  @Input() candidates: Array<Voter> = [];
  @Input() assessors: Assessor[] = [];
  @Input() isAssessorsValidated: boolean = false;
  @Output() assessorsChange = new EventEmitter<Assessor[]>();
  @Output() assessorsValidated = new EventEmitter<void>();
  president: Voter | null = null;
  selectedAssessors: Array<Voter> = [];
  availableCandidates: Array<Voter> = [];
  showPresidentSearch: boolean = false;
  showAssessorSearch: boolean = false;
  presidentSearchTerm: string = '';
  assessorSearchTerm: string = '';
  isValidated: boolean = false;
  constructor() { }

  canValidate(): boolean {
    return this.president !== null && this.selectedAssessors.length >= 2 && this.selectedAssessors.length <= 5;
  }

  validateAssessors() {
    if (this.canValidate()) {
      this.isValidated = true;
      this.assessorsValidated.emit();
    }
  }

  restoreAssessorsFromInput() {
    if (this.assessors && this.assessors.length > 0) {
      // Le premier assesseur est toujours le président
      const presidentAssessor = this.assessors[0];
      const presidentCandidate = this.candidates.find(c => c.email === presidentAssessor.email);
      
      if (presidentCandidate) {
        this.president = presidentCandidate;
        // Retirer le président de availableCandidates
        const presidentIndex = this.availableCandidates.indexOf(presidentCandidate);
        if (presidentIndex > -1) {
          this.availableCandidates.splice(presidentIndex, 1);
        }
      }

      // Les autres assesseurs (indices 1+)
      for (let i = 1; i < this.assessors.length; i++) {
        const assessor = this.assessors[i];
        const assessorCandidate = this.candidates.find(c => c.email === assessor.email);
        
        if (assessorCandidate) {
          this.selectedAssessors.push(assessorCandidate);
          // Retirer l'assesseur de availableCandidates
          const assessorIndex = this.availableCandidates.indexOf(assessorCandidate);
          if (assessorIndex > -1) {
            this.availableCandidates.splice(assessorIndex, 1);
          }
        }
      }

      // Si nous avons restauré des assesseurs et que isAssessorsValidated est true, marquer comme validé
      if (this.isAssessorsValidated && this.canValidate()) {
        this.isValidated = true;
      }
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['candidates'] && !changes['candidates'].firstChange) {
      this.availableCandidates = [...this.candidates];
      
      // Restaurer les assesseurs si nécessaire
      if (this.assessors && this.assessors.length > 0) {
        // Réinitialiser l'état avant de restaurer
        this.president = null;
        this.selectedAssessors = [];
        this.restoreAssessorsFromInput();
      }
    }
    
    if (changes['assessors'] && !changes['assessors'].firstChange) {
      // Si les assesseurs ont changé, restaurer
      this.president = null;
      this.selectedAssessors = [];
      this.availableCandidates = [...this.candidates];
      this.restoreAssessorsFromInput();
    }
    
    if (changes['isAssessorsValidated'] && this.isAssessorsValidated && this.canValidate()) {
      this.isValidated = true;
    }
  }

  ngOnInit(): void {
    this.availableCandidates = [...this.candidates];
    // Restaurer les assesseurs depuis l'input si présents (uniquement si pas déjà restaurés)
    if (this.selectedAssessors.length === 0 && this.president === null) {
      this.restoreAssessorsFromInput();
    }
  }

  addPresident() {
    this.showPresidentSearch = true;
  }

  addAssessor() {
    this.showAssessorSearch = true;
  }

  getFilteredCandidatesForPresident(): Array<Voter> {
    if (!this.presidentSearchTerm) {
      return this.availableCandidates;
    }
    const term = this.presidentSearchTerm.toLowerCase();
    return this.availableCandidates.filter(candidate => 
      (candidate.firstName && candidate.firstName.toLowerCase().includes(term)) ||
      (candidate.lastName && candidate.lastName.toLowerCase().includes(term)) ||
      (candidate.email && candidate.email.toLowerCase().includes(term))
    );
  }

  getFilteredCandidatesForAssessors(): Array<Voter> {
    if (!this.assessorSearchTerm) {
      return this.availableCandidates;
    }
    const term = this.assessorSearchTerm.toLowerCase();
    return this.availableCandidates.filter(candidate => 
      (candidate.firstName && candidate.firstName.toLowerCase().includes(term)) ||
      (candidate.lastName && candidate.lastName.toLowerCase().includes(term)) ||
      (candidate.email && candidate.email.toLowerCase().includes(term))
    );
  }

  selectPresident(candidate: Voter) {
    // Retirer le candidat de la liste des candidats disponibles
    const index = this.availableCandidates.indexOf(candidate);
    if (index > -1) {
      this.availableCandidates.splice(index, 1);
    }
    
    // Définir le président
    this.president = candidate;
    
    // Ajouter à la liste des assesseurs
    const assessor = new Assessor(
      `${candidate.firstName} ${candidate.lastName}`,
      candidate.email
    );
    this.assessors.push(assessor);
    
    // Cacher le champ de recherche et réinitialiser le terme de recherche
    this.showPresidentSearch = false;
    this.presidentSearchTerm = '';
    
    // Notifier le parent du changement
    this.assessorsChange.emit(this.assessors);
  }

  removePresident() {
    if (this.president) {
      // Remettre le président dans la liste des candidats disponibles
      this.availableCandidates.push(this.president);
      
      // Retirer de la liste des assesseurs
      const assessorIndex = this.assessors.findIndex(a => 
        a.email === this.president.email
      );
      if (assessorIndex > -1) {
        this.assessors.splice(assessorIndex, 1);
      }
      
      this.president = null;
      
      // Notifier le parent du changement
      this.assessorsChange.emit(this.assessors);
    }
  }

  selectAssessor(candidate: Voter) {
    // Vérifier qu'on ne dépasse pas 5 assesseurs
    if (this.selectedAssessors.length >= 5) {
      return;
    }
    
    // Retirer le candidat de la liste des candidats disponibles
    const index = this.availableCandidates.indexOf(candidate);
    if (index > -1) {
      this.availableCandidates.splice(index, 1);
    }
    
    // Ajouter l'assesseur à la liste des assesseurs sélectionnés
    this.selectedAssessors.push(candidate);
    
    // Ajouter à la liste des assesseurs
    const assessor = new Assessor(
      `${candidate.firstName} ${candidate.lastName}`,
      candidate.email
    );
    this.assessors.push(assessor);
    
    // Cacher le champ de recherche et réinitialiser le terme de recherche
    this.showAssessorSearch = false;
    this.assessorSearchTerm = '';
    
    // Notifier le parent du changement
    this.assessorsChange.emit(this.assessors);
  }

  removeAssessor(candidate: Voter) {
    // Retirer l'assesseur de la liste des assesseurs sélectionnés
    const index = this.selectedAssessors.indexOf(candidate);
    if (index > -1) {
      this.selectedAssessors.splice(index, 1);
    }
    
    // Remettre le candidat dans la liste des candidats disponibles
    this.availableCandidates.push(candidate);
    
    // Retirer de la liste des assesseurs
    const assessorIndex = this.assessors.findIndex(a => 
      a.email === candidate.email
    );
    if (assessorIndex > -1) {
      this.assessors.splice(assessorIndex, 1);
    }
     
    // Notifier le parent du changement
    this.assessorsChange.emit(this.assessors);
  }
}
